<template>
  <div class="tw-flex tw-flex-col tw-items-center tw-gap-2">
    <div v-if="hasTime" class="tw-h-6 tw-flex tw-items-center tw-justify-center">
      <WdsTooltip v-if="hasClickableTimesheet" :text="$t('View timesheet')" :global="tableGlobals">
        <WdsIcon
          name="launch"
          :size="16"
          class="tw-cursor-pointer tw-text-gray-600 hover:tw-text-gray-800"
          @click="showTimesheetModal = true"
        />
      </WdsTooltip>
      <WdsTooltip
        v-else-if="hasDownloadableTimesheetFile"
        :text="$t('Download file')"
        :global="tableGlobals"
      >
        <WdsIcon
          name="download"
          :size="16"
          class="tw-cursor-pointer tw-text-gray-600 hover:tw-text-gray-800"
          data-test="payment-request-timesheet-file-download"
          @click="downloadTimesheetFile"
        />
      </WdsTooltip>
      <span v-else class="tw-text-gray-400">–</span>
    </div>
    <div v-if="hasAmount" class="tw-h-6 tw-flex tw-items-center tw-justify-center">
      <span class="tw-text-gray-400">–</span>
    </div>
    <div v-if="hasMilestone" class="tw-h-6 tw-flex tw-items-center tw-justify-center">
      <span class="tw-text-gray-400">–</span>
    </div>
    <div v-if="hasExpenses" class="tw-h-6 tw-flex tw-items-center tw-justify-center">
      <WdsTooltip v-if="hasExpenseReports" :text="$t('View expenses')" :global="tableGlobals">
        <WdsIcon
          name="launch"
          :size="16"
          class="tw-cursor-pointer tw-text-gray-600 hover:tw-text-gray-800"
          @click="showExpenseFilesModal = true"
        />
      </WdsTooltip>
      <span v-else class="tw-text-gray-400">–</span>
    </div>
  </div>

  <PaymentRequestTimesheetModal
    v-if="hasClickableTimesheet"
    :show="showTimesheetModal"
    :payment-request-id="column.id"
    @close="showTimesheetModal = false"
  />

  <PaymentRequestExpenseFilesModal
    v-if="hasExpenseReports"
    :show="showExpenseFilesModal"
    :payment-request-id="column.id"
    @close="showExpenseFilesModal = false"
  />
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import gql from 'graphql-tag'
import { useApolloClient } from '@vue/apollo-composable'
import { WdsIcon, WdsTooltip } from '@worksome/wds-components'
import PaymentRequestTimesheetModal from './PaymentRequestTimesheetModal.vue'
import PaymentRequestExpenseFilesModal from './PaymentRequestExpenseFilesModal.vue'
import { FragmentType, graphql, useFragment } from '@/gql'
import { useTranslation } from '@/composables/translation'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import { useTableGlobalsSelector } from '@/components/GraphqlDataTable/injection'

const tableGlobals = useTableGlobalsSelector()

const { $t } = useTranslation()

const PaymentRequestDetailsColumn = graphql(`
  fragment PaymentRequestDetailsColumn on PaymentRequest {
    id
    rateType
    billedAmount
    expensesAmount
    legacyTimesheet {
      html
    }
    timesheet {
      id
    }
    milestones {
      paginatorInfo {
        total
      }
    }
    expenseReports {
      paginatorInfo {
        total
      }
    }
  }
`)

const props = defineProps<{
  row: FragmentType<typeof PaymentRequestDetailsColumn>
}>()

const column = computed(() => useFragment(PaymentRequestDetailsColumn, props.row))
const showTimesheetModal = ref(false)
const showExpenseFilesModal = ref(false)
const { client } = useApolloClient('publicApi')

const isTimeBased = computed(() => {
  const rateType = column.value.rateType?.toLowerCase()
  return ['hourly', 'daily', 'weekly', 'monthly'].includes(rateType || '')
})

const hasBilledAmount = computed(() => column.value.billedAmount > 0)
const hasMilestone = computed(() => (column.value.milestones?.paginatorInfo?.total ?? 0) > 0)
const hasTime = computed(() => !hasMilestone.value && isTimeBased.value && hasBilledAmount.value)
const hasAmount = computed(() => !hasMilestone.value && !isTimeBased.value && hasBilledAmount.value)
const hasExpenses = computed(() => column.value.expensesAmount > 0)
const hasTimesheet = computed(() => !!column.value.timesheet?.id)
const hasLegacyTimesheet = computed(() => !!column.value.legacyTimesheet?.html)
const hasClickableTimesheet = computed(
  () => hasTime.value && (hasTimesheet.value || hasLegacyTimesheet.value),
)
const hasDownloadableTimesheetFile = computed(() => hasTime.value && !hasClickableTimesheet.value)

const hasExpenseReports = computed(
  () => (column.value.expenseReports?.paginatorInfo?.total ?? 0) > 0,
)

const PaymentRequestTimesheetFileQuery = gql`
  query PaymentRequestTimesheetFileQuery($id: ID!) {
    paymentRequest(id: $id) {
      id
      files(first: 1) {
        data {
          id
          url
        }
      }
    }
  }
`

const downloadTimesheetFile = async () => {
  if (typeof window === 'undefined') {
    return
  }

  const paymentRequestId = column.value.id
  if (!paymentRequestId) {
    return
  }

  const { data } = await client.query({
    query: PaymentRequestTimesheetFileQuery,
    variables: { id: paymentRequestId },
    fetchPolicy: 'network-only',
  })

  const file = data?.paymentRequest?.files?.data?.[0]
  const fileId = file?.id
  const fileUrl = file?.url
  const paymentRequestLocalId = (() => {
    try {
      const decoded = atob(paymentRequestId)
      return decoded.split(':').pop() ?? ''
    } catch {
      return ''
    }
  })()

  const storageNameFromSignedUrl = (() => {
    if (!fileUrl) return ''
    try {
      const url = new URL(fileUrl, window.location.origin)
      const segments = url.pathname.split('/').filter(Boolean)
      return segments[segments.length - 1] ?? ''
    } catch {
      return ''
    }
  })()

  const billFileDownloadUrl =
    paymentRequestLocalId && storageNameFromSignedUrl
      ? `/bills/${paymentRequestLocalId}/files/${encodeURIComponent(storageNameFromSignedUrl)}`
      : ''

  const downloadUrl = billFileDownloadUrl
    || (fileId ? laravelNamedRoute(namedRoutes.files.download, { fileId }) : fileUrl)

  if (!downloadUrl) {
    return
  }

  const a = document.createElement('a')
  a.href = downloadUrl
  a.rel = 'noopener'
  a.target = '_blank'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}
</script>


